import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pencil, Plus, RefreshCw, Send, Eye, Trash2, Undo2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { useConfirmDialog } from "../components/ConfirmDialogProvider";
import { RichTextField, cleanHtmlForSave } from "../components/resourcePerson/RichTextField";
import { StringListEditor, TableOfContentsEditor } from "../components/resourcePerson/MetadataEditors";
import UploadField from "../components/resourcePerson/UploadField";
import { useResourcePersonMarketplace } from "../store/resourcePerson/resourcePersonStore";
import { MARKETPLACE_TYPES } from "../store/resourcePerson/resourcePersonMarketplaceStore";
import { parseTableOfContents, stringifyTableOfContents } from "../utils/resourceMetadata";

const TYPE_LABELS = {
  "project-topics": "Project Topics",
  "seminar-topics": "Seminar Topics",
  "research-materials": "Research Materials",
  manuals: "Manuals",
};

const STATUSES = ["", "DRAFT", "PENDING", "APPROVED", "REJECTED", "DISABLED", "WITHDRAWN"];
const ACADEMIC_LEVELS = ["", "ND", "HND", "NCE", "BSC", "MSC", "PGD", "PHD", "OTHER"];
const FILE_FORMATS = ["", "PDF", "DOCX", "OTHER"];

const statusClass = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "APPROVED":
      return "bg-emerald-50 text-emerald-700";
    case "PENDING":
      return "bg-amber-50 text-amber-700";
    case "REJECTED":
    case "DISABLED":
      return "bg-rose-50 text-rose-700";
    case "WITHDRAWN":
      return "bg-slate-200 text-slate-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
};

const EMPTY_FORM = {
  title: "",
  summary: "",
  description: "",
  abstract: "",
  chapterOnePreview: "",
  tableOfContents: "",
  whatIsIncluded: "",
  academicLevel: "",
  fieldOfStudy: "",
  fileFormat: "",
  price: "",
  coverImage: "",
  fileUrl: "",
  filePublicId: "",
  fileCloudType: "",
  fileDeliveryType: "",
};

const fieldClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";

const toForm = (item = {}) => ({
  ...EMPTY_FORM,
  ...Object.fromEntries(
    Object.keys(EMPTY_FORM).map((key) => [
      key,
      key === "whatIsIncluded"
        ? (item.whatIsIncluded || []).join("\n")
        : key === "tableOfContents"
          ? stringifyTableOfContents(item.tableOfContents)
          : item[key] ?? "",
    ])
  ),
});

const toPayload = (form) => {
  const payload = {};
  const tableOfContents = parseTableOfContents(form.tableOfContents);
  if (tableOfContents.length) payload.tableOfContents = tableOfContents;
  Object.entries(form).forEach(([key, value]) => {
    if (key === "tableOfContents") return;
    if (key === "whatIsIncluded") {
      const items = String(value)
        .split("\n")
        .map((entry) => entry.trim())
        .filter(Boolean);
      if (items.length) payload.whatIsIncluded = items;
      return;
    }
    if (key === "price") {
      if (value !== "" && !Number.isNaN(Number(value))) payload.price = Number(value);
      return;
    }
    if (["summary", "description", "abstract", "chapterOnePreview"].includes(key)) {
      const html = cleanHtmlForSave(value);
      if (html) payload[key] = html;
      return;
    }
    const text = String(value ?? "").trim();
    if (text) payload[key] = text;
  });
  return payload;
};

export default function ResourcePersonMarketplace() {
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialType = MARKETPLACE_TYPES.includes(searchParams.get("type"))
    ? searchParams.get("type")
    : MARKETPLACE_TYPES[0];

  const {
    marketplaceItems,
    marketplaceLoading,
    marketplaceError,
    marketplaceTotal,
    marketplacePages,
    marketplaceItem,
    marketplaceItemLoading,
    marketplaceItemError,
    marketplaceMutationLoading,
    marketplaceMutationError,
    fetchMarketplaceItems,
    fetchMarketplaceItem,
    createMarketplaceItem,
    updateMarketplaceItem,
    deleteMarketplaceItem,
    submitMarketplaceItem,
    unsubmitMarketplaceItem,
    clearMarketplaceItem,
  } = useResourcePersonMarketplace();

  const [type, setType] = useState(initialType);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [message, setMessage] = useState("");

  const notify = useCallback((text) => {
    setMessage(text);
    setTimeout(() => setMessage(""), 5000);
  }, []);

  const load = useCallback(async () => {
    const result = await fetchMarketplaceItems(type, {
      page,
      limit: 10,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      sort,
    });
    if (!result.success) notify(result.error || "Could not load marketplace items.");
  }, [fetchMarketplaceItems, notify, page, search, sort, status, type]);

  useEffect(() => {
    load();
  }, [load]);

  const selectType = (nextType) => {
    setType(nextType);
    setPage(1);
    setFormOpen(false);
    clearMarketplaceItem();
  };

  const openCreate = () => {
    navigate(`/resource-person/marketplace/new?type=${encodeURIComponent(type)}`);
  };

  const openEdit = async (item) => {
    setEditingId(item.id);
    setForm(toForm(item));
    setFormOpen(true);
    const result = await fetchMarketplaceItem(type, item.id);
    if (result.success && result.item) setForm(toForm(result.item));
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    clearMarketplaceItem();
  };

  const save = async (event) => {
    event.preventDefault();
    if (!String(form.title).trim()) return notify("A title is required.");
    let payload;
    try {
      payload = toPayload(form);
    } catch (err) {
      return notify(err.message || "Check the table of contents JSON.");
    }
    const result = editingId
      ? await updateMarketplaceItem(type, editingId, payload)
      : await createMarketplaceItem(type, payload);
    if (!result.success) return notify(result.error || "Could not save this item.");
    notify(editingId ? "Marketplace item updated." : "Marketplace item created.");
    closeForm();
    await load();
  };

  const remove = (item) =>
    confirm({
      title: `Delete "${item.title}"?`,
      description: "Only unsubmitted DRAFT or REJECTED items without purchases can be deleted.",
      confirmLabel: "Delete item",
      onConfirm: async () => {
        const result = await deleteMarketplaceItem(type, item.id);
        if (!result.success) throw new Error(result.error || "Could not delete this item.");
        notify("Marketplace item deleted.");
        await load();
      },
    });

  const submit = (item) =>
    confirm({
      title: `Submit "${item.title}" for review?`,
      description: "The item will be sent to the moderation queue.",
      confirmLabel: "Submit",
      onConfirm: async () => {
        const result = await submitMarketplaceItem(type, item.id);
        if (!result.success) throw new Error(result.error || "Could not submit this item.");
        notify("Marketplace item submitted.");
        await load();
      },
    });

  const unsubmit = async (item) => {
    const result = await unsubmitMarketplaceItem(type, item.id);
    if (!result.success) return notify(result.error || "Could not withdraw this item.");
    notify("Marketplace item returned to draft.");
    await load();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="mb-1 text-4xl font-extrabold tracking-tight">Marketplace Resources</h1>
          <p className="text-muted-foreground">
            Create and manage project topics, seminar topics, research materials and manuals.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded bg-blue-900 px-5 py-2 font-medium text-white hover:bg-blue-800">
          <Plus className="mr-2 h-4 w-4" /> New item
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {MARKETPLACE_TYPES.map((value) => (
          <Button
            key={value}
            variant={type === value ? "default" : "outline"}
            onClick={() => selectType(value)}
            className={type === value ? "bg-blue-900 text-white hover:bg-blue-800" : ""}
          >
            {TYPE_LABELS[value]}
          </Button>
        ))}
      </div>

      {message ? (
        <p className="m-0 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {message}
        </p>
      ) : null}
      {marketplaceMutationError ? (
        <p className="m-0 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {marketplaceMutationError}
        </p>
      ) : null}

      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <input
              className={`${fieldClass} max-w-xs`}
              placeholder="Search by title"
              value={search}
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
            />
            <select
              className={`${fieldClass} max-w-[180px]`}
              value={status}
              onChange={(event) => {
                setPage(1);
                setStatus(event.target.value);
              }}
            >
              {STATUSES.map((value) => (
                <option key={value || "all"} value={value}>
                  {value || "All statuses"}
                </option>
              ))}
            </select>
            <select
              className={`${fieldClass} max-w-[190px]`}
              value={sort}
              onChange={(event) => {
                setPage(1);
                setSort(event.target.value);
              }}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="price_asc">Price (low to high)</option>
              <option value="price_desc">Price (high to low)</option>
            </select>
            <Button variant="outline" onClick={load} className="ml-auto">
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
          </div>

          {marketplaceError ? (
            <p className="m-0 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {marketplaceError}
            </p>
          ) : null}

          {marketplaceLoading && marketplaceItems.length === 0 ? (
            <div className="grid gap-3">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-20 w-full" />
              ))}
            </div>
          ) : marketplaceItems.length === 0 ? (
            <p className="m-0 py-10 text-center text-sm text-slate-500">
              No {TYPE_LABELS[type].toLowerCase()} yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Title</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Price</th>
                    <th className="px-4 py-3 font-semibold">Downloads</th>
                    <th className="px-4 py-3 font-semibold">Updated</th>
                    <th className="px-4 py-3 text-center font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {marketplaceItems.map((item) => {
                    const state = String(item.status || "DRAFT").toUpperCase();
                    const canSubmit = state === "DRAFT" || state === "REJECTED";
                    return (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="m-0 font-semibold text-slate-900">{item.title}</p>
                          <p className="m-0 text-xs text-slate-500">{item.slug || item.id}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`font-semibold ${statusClass(state)}`}>{state}</Badge>
                        </td>
                        <td className="px-4 py-3 text-slate-700">₦{Number(item.price || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-600">{item.downloads ?? 0}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap justify-center gap-1.5">
                            <Button variant="outline" size="sm" onClick={() => openEdit(item)} title="Edit and view">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {canSubmit ? (
                              <Button variant="outline" size="sm" onClick={() => submit(item)} title="Submit for review">
                                <Send className="h-4 w-4" />
                              </Button>
                            ) : null}
                            {state === "PENDING" ? (
                              <Button variant="outline" size="sm" onClick={() => unsubmit(item)} title="Return to draft">
                                <Undo2 className="h-4 w-4" />
                              </Button>
                            ) : null}
                            {canSubmit ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600"
                                onClick={() => remove(item)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-sm text-slate-500">
            <span>
              Page {page} of {marketplacePages || 1} · {marketplaceTotal || 0} items
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= (marketplacePages || 1)}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {formOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4">
          <form
            onSubmit={save}
            className="my-auto w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl"
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="m-0 text-xl font-bold text-slate-900">
                  {editingId ? "Edit" : "New"} {TYPE_LABELS[type].replace(/s$/, "")}
                </h2>
                {marketplaceItemLoading ? (
                  <p className="m-0 mt-1 text-xs text-slate-500">Loading full details…</p>
                ) : null}
                {marketplaceItemError ? (
                  <p className="m-0 mt-1 text-xs text-red-600">{marketplaceItemError}</p>
                ) : null}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={closeForm}>
                Close
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-slate-600 sm:col-span-2">
                Title
                <input
                  className={`${fieldClass} mt-1`}
                  required
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                />
              </label>

              {[
                ["fieldOfStudy", "Field of study"],
                ["fileUrl", "File URL"],
                ["price", "Price"],
                ["filePublicId", "File public ID"],
                ["fileCloudType", "File cloud type"],
                ["fileDeliveryType", "File delivery type"],
              ].map(([key, label]) => (
                <label key={key} className="text-xs font-semibold text-slate-600">
                  {label}
                  <input
                    className={`${fieldClass} mt-1`}
                    type={key === "price" ? "number" : "text"}
                    value={form[key]}
                    onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                  />
                </label>
              ))}

              <div className="text-xs font-semibold text-slate-600 sm:col-span-2">
                Cover image
                <UploadField
                  kind="image"
                  folder="marketplace-covers"
                  label=""
                  accept="image/*"
                  value={form.coverImage}
                  onChange={(coverImage) =>
                    setForm((current) => ({ ...current, coverImage }))
                  }
                />
              </div>

              <label className="text-xs font-semibold text-slate-600">
                Academic level
                <select
                  className={`${fieldClass} mt-1`}
                  value={form.academicLevel}
                  onChange={(event) => setForm({ ...form, academicLevel: event.target.value })}
                >
                  {ACADEMIC_LEVELS.map((value) => (
                    <option key={value || "none"} value={value}>
                      {value || "Not set"}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs font-semibold text-slate-600">
                File format
                <select
                  className={`${fieldClass} mt-1`}
                  value={form.fileFormat}
                  onChange={(event) => setForm({ ...form, fileFormat: event.target.value })}
                >
                  {FILE_FORMATS.map((value) => (
                    <option key={value || "none"} value={value}>
                      {value || "Not set"}
                    </option>
                  ))}
                </select>
              </label>

              <div className="text-xs font-semibold text-slate-600 sm:col-span-2">
                Summary
                <RichTextField
                  className="mt-1"
                  value={form.summary}
                  minHeight={80}
                  maxHeight={160}
                  placeholder="A short listing summary."
                  onChange={(summary) => setForm((current) => ({ ...current, summary }))}
                />
              </div>

              {[
                ["description", "Description", 140],
                ["abstract", "Abstract", 100],
                ["chapterOnePreview", "Chapter one preview", 100],
              ].map(([key, label, height]) => (
                <div key={key} className="text-xs font-semibold text-slate-600 sm:col-span-2">
                  {label}
                  <RichTextField
                    className="mt-1"
                    value={form[key]}
                    minHeight={height}
                    maxHeight={240}
                    onChange={(value) => setForm((current) => ({ ...current, [key]: value }))}
                  />
                </div>
              ))}

              <div className="text-xs font-semibold text-slate-600 sm:col-span-2">
                Table of contents
                <TableOfContentsEditor
                  value={form.tableOfContents}
                  onChange={(tableOfContents) =>
                    setForm((current) => ({ ...current, tableOfContents }))
                  }
                />
              </div>

              <div className="text-xs font-semibold text-slate-600 sm:col-span-2">
                What is included
                <StringListEditor
                  value={form.whatIsIncluded}
                  onChange={(whatIsIncluded) =>
                    setForm((current) => ({ ...current, whatIsIncluded }))
                  }
                  placeholder="e.g. Full project documentation"
                />
              </div>
            </div>

            {marketplaceItem && editingId ? (
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <p className="m-0 flex items-center gap-2">
                  <Eye className="h-4 w-4" /> Status: {marketplaceItem.status || "DRAFT"} · Downloads:{" "}
                  {marketplaceItem.downloads ?? 0} · Revenue: ₦
                  {Number(marketplaceItem.revenue || 0).toLocaleString()}
                </p>
                {marketplaceItem.rejectionReason ? (
                  <p className="m-0 mt-1 text-red-600">Rejection reason: {marketplaceItem.rejectionReason}</p>
                ) : null}
              </div>
            ) : null}

            <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
              <Button type="button" variant="outline" onClick={closeForm}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={marketplaceMutationLoading}
                className="bg-blue-900 text-white hover:bg-blue-800"
              >
                {marketplaceMutationLoading ? "Saving…" : editingId ? "Save changes" : "Create draft"}
              </Button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
